// ═══════════════════════════════════════════════════════════════════
// BIRTHDAY SALE 2026 — Main Script
// ═══════════════════════════════════════════════════════════════════
//
// FLOW:
//   ATC click (existing <add-to-cart>) → drawer opens (untouched)
//   ajaxProduct:added event → show qty selector on that card
//   + click → qty++ + open drawer
//   - click → qty-- silently | qty=0 → show ATC back
//
// LIVE:
//   updateProgressBars()   → collection page fixed bar (JS) + mini-cart (Liquid re-render)
//   updatePremiumCap()     → hide premium ATC cards when cap hit
//
// FUTURE DEV:
//   BS_STATE.normalCount   → bundle pricing logic
//   BS_CONFIG.buzzOffer    → ₹80 off timer (8:00–9:20 PM, July 2–5)
// ═══════════════════════════════════════════════════════════════════

(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────────────
  // CONFIG — change values here, not in logic below
  // ─────────────────────────────────────────────────────────────────
  var BS_CONFIG = {
    collectionHandle: 'birthday-sale',
    bundle: {
      b2Price: 799,
      b4Price: 1399,
      extraPrice: 449
    },
    premiumCap: {
      b2: 1,   // max 1 premium in 2-item bundle
      b4: 2    // max 2 premium in 4-item bundle
    },
    buzzOffer: {
      discountAmount: 80,
      startHour: 20,      // 8:00 PM
      endHour: 21,
      endMinute: 20,      // 9:20 PM
      activeDates: ['2026-07-02', '2026-07-03', '2026-07-04', '2026-07-05']
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // STATE — single source of truth for birthday sale cart state
  // ─────────────────────────────────────────────────────────────────
  var BS_STATE = {
    cartItems: [],      // [{ variantId, qty, isPremium }] — birthday sale items only
    normalCount: 0,     // total normal product qty in cart
    premiumCount: 0,    // total premium product qty in cart
    totalCount: 0,      // normalCount + premiumCount
    validCount: 0,      // normalCount + min(premiumCount, premiumCap.b4) — items that count toward deal
    bsSavings: 0        // total Birthday Sale discount amount (in paise)
  };

  // ─────────────────────────────────────────────────────────────────
  // PRODUCT MAP — variantId → isPremium, fetched from collection API
  // Used by syncState so counts are cart-based, not DOM-based.
  // Cached in sessionStorage to avoid repeat fetches on navigation.
  // ─────────────────────────────────────────────────────────────────
  var BS_PRODUCT_MAP = null;

  function initProductMap() {
    var cached = sessionStorage.getItem('bs_product_map_v2');
    if (cached) {
      try { BS_PRODUCT_MAP = JSON.parse(cached); return; } catch (e) {}
    }
    var handles = [BS_CONFIG.collectionHandle];
    Promise.all(handles.map(function (h) {
      return fetch('/collections/' + h + '/products.json?limit=250&fields=variants,tags')
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (d) { return d.products || []; })
        .catch(function () { return []; });
    })).then(function (results) {
      var map = {};
      results.forEach(function (products) {
        products.forEach(function (product) {
          var tags = product.tags || [];
          var isPremium = Array.isArray(tags)
            ? tags.indexOf('premium') !== -1
            : String(tags).split(',').map(function (t) { return t.trim(); }).indexOf('premium') !== -1;
          (product.variants || []).forEach(function (v) {
            map[v.id] = {
              isPremium: isPremium,
              price: Math.round(parseFloat(v.price) * 100) || 0,
              compareAtPrice: v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : 0
            };
          });
        });
      });
      BS_PRODUCT_MAP = map;
      try { sessionStorage.setItem('bs_product_map_v2', JSON.stringify(map)); } catch (e) {}
      // Re-run syncState (not just updateUI) — BS_STATE.bsSavings/counts may have been
      // computed earlier with an empty map (fetch race), so they need recalculating now
      // that BS_PRODUCT_MAP is actually populated. Only if BS progress bar is visible —
      // avoids late DOM mutations that reset GokWik's MutationObserver debounce and
      // block the checkout button.
      if (document.querySelector('.bs-pb')) syncState();
    });
  }

  // ─────────────────────────────────────────────────────────────────
  // CART HELPERS
  // ─────────────────────────────────────────────────────────────────

  var miniCart = document.querySelector('mini-cart');

  // True only on birthday sale collection pages — guards drawer control logic
  var IS_BS_PAGE = window.location.pathname.indexOf('birthday-sale') !== -1;

  // Patch miniCart.renderContents — intercept before drawer opens
  // parsedState from /cart/add.js = single added item (NOT full cart)
  // So use parsedState.variant_id + DOM bsIds to detect BS add
  // and BS_STATE.totalCount (pre-add) + 1 = new total
  if (IS_BS_PAGE && miniCart) {
    var _origRenderContents = miniCart.renderContents.bind(miniCart);
    miniCart.renderContents = function (parsedState) {
      var addedVariantId = String(parsedState && (parsedState.variant_id || parsedState.id) || '');
      // Is this a birthday sale product? Check against page DOM variant IDs
      var isBsProduct = !!document.querySelector(
        '.bs-atc-btn[data-variant-id="' + addedVariantId + '"],' +
        '.bs-qty-selector[data-variant-id="' + addedVariantId + '"]'
      );
      if (isBsProduct) {
        var newTotal = BS_STATE.totalCount + 1;
        if (!shouldOpenDrawer(newTotal)) {
          // Silent: update sections (cart bubble count) but don't open drawer
          miniCart.productId = parsedState.id;
          miniCart.getSectionsToRender().forEach(function (section) {
            var el = document.getElementById(section.elementId || section.id);
            if (el && parsedState.sections && parsedState.sections[section.id]) {
              el.innerHTML = miniCart.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
            }
          });
          return;
        }
      }
      _origRenderContents(parsedState);
    };
  }

  function fetchConfig(type) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/' + type + ', text/' + type
      }
    };
  }

  // Update cart qty — first fetches /cart.js to get line number,
  // then uses line (not variant id) exactly like CartItems.updateQuantity in cart.js
  //
  // Shopify keeps a variant split across multiple cart lines when line item
  // properties differ between /cart/add.js calls (e.g. the first add via the
  // theme's native <add-to-cart> form vs. our own plain-JSON cartAdd() calls).
  // qty here is the desired TOTAL across all of that variant's lines, so find
  // every matching line and adjust the last one so the total comes out right.
  function cartChange(variantId, qty) {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var line = 0;
        var otherLinesTotal = 0;
        var lastLineQty = 0;
        for (var i = 0; i < cart.items.length; i++) {
          if (String(cart.items[i].variant_id) === String(variantId)) {
            if (line) otherLinesTotal += lastLineQty;
            line = i + 1; // 1-based line index
            lastLineQty = cart.items[i].quantity;
          }
        }
        if (!line) return Promise.reject(new Error('BS: variant not in cart'));

        var newLineQty = Math.max(0, qty - otherLinesTotal);

        var sections = miniCart ? miniCart.getSectionsToRender().map(function (s) { return s.id; }) : [];
        return fetch(window.routes.cart_change_url, Object.assign(fetchConfig('javascript'), {
          body: JSON.stringify({
            line: line,
            quantity: newLineQty,
            sections: sections,
            sections_url: window.location.pathname
          })
        })).then(function (r) { return r.json(); });
      });
  }

  // Add to cart with sections — returns Promise with parsedState (for drawer)
  function cartAdd(variantId) {
    var sections = miniCart ? miniCart.getSectionsToRender().map(function (s) { return s.id; }) : [];
    var body = JSON.stringify({
      id: parseInt(variantId),
      quantity: 1,
      sections: sections,
      sections_url: window.location.pathname
    });
    return fetch(window.routes.cart_add_url, Object.assign(fetchConfig('javascript'), { body: body }))
      .then(function (r) { return r.json(); });
  }

  // Drawer opens only at milestones: qty 2, then 4 and every item after
  function shouldOpenDrawer(newTotal) {
    return newTotal === 2 || newTotal >= 4;
  }

  // Open mini-cart drawer using existing system
  function openDrawer(parsedState) {
    if (miniCart && parsedState && parsedState.sections) {
      miniCart.renderContents(parsedState);
    } else if (miniCart) {
      miniCart.open();
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // STATE SYNC — fetch cart and update BS_STATE
  // Uses DOM (.bs-qty-selector variantIds) to identify birthday sale
  // items — more reliable than checking product_tags from cart API
  // ─────────────────────────────────────────────────────────────────
  function syncState() {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Build variantId → qty map from cart. Sum (not overwrite) because
        // Shopify can keep the same variant split across multiple lines when
        // line item properties differ between /cart/add.js calls.
        var cartQtyMap = {};
        cart.items.forEach(function (item) {
          cartQtyMap[item.variant_id] = (cartQtyMap[item.variant_id] || 0) + item.quantity;
        });

        var bsItems = [];
        var normal = 0;
        var premium = 0;

        var _syncSeen = {};
        document.querySelectorAll('.bs-qty-selector').forEach(function (selector) {
          var variantId = parseInt(selector.dataset.variantId);
          if (!variantId || _syncSeen[variantId]) return;
          var qty = cartQtyMap[variantId] || 0;
          if (qty === 0) return;
          _syncSeen[variantId] = true;
          var card = selector.closest('.card-wrapper') || selector.closest('li');
          var isPremium = !!(card && (
            card.dataset.bsPremium === 'true' || card.querySelector('.plp-badge-premium')
          ));
          bsItems.push({ variantId: variantId, qty: qty, isPremium: isPremium });
          if (isPremium) { premium += qty; } else { normal += qty; }
        });

        // Fallback for non-collection pages: read Liquid-rendered counts from mini-cart
        if (normal === 0 && premium === 0) {
          var countsEl = document.getElementById('bs-cart-counts');
          if (countsEl) {
            normal = parseInt(countsEl.dataset.bsNormal || '0', 10);
            premium = parseInt(countsEl.dataset.bsPremium || '0', 10);
          }
        }

        // If product map is ready, recount from actual cart data (catches
        // variants not visible on page / different from PLP default variant)
        // if (BS_PRODUCT_MAP && Object.keys(BS_PRODUCT_MAP).length > 0) {
        //   normal = 0; premium = 0;
        //   cart.items.forEach(function (item) {
        //     if (BS_PRODUCT_MAP.hasOwnProperty(item.variant_id)) {
        //       if (BS_PRODUCT_MAP[item.variant_id]) premium += item.quantity;
        //       else normal += item.quantity;
        //     }
        //   });
        // }

        // var savings = 0;
        // cart.items.forEach(function (item) {
        //   if (!item.discounts) return;
        //   item.discounts.forEach(function (d) {
        //     if (d.title && d.title.indexOf('Birthday Sale') !== -1) savings += d.amount;
        //   });
        // });

        // Savings = MRP (compare_at_price) minus what's actually paid, summed only across
        // items that actually HAVE the Birthday Sale discount applied (same scope as
        // Liquid's _item_is_bs in mini-cart-revamp.liquid — collection membership alone
        // isn't enough). Uses final_line_price (falls back to line_price) — NOT item.price
        // — because item.price excludes cart/order-level automatic discounts.
        var savings = 0;
        cart.items.forEach(function (item) {
          var hasBsDiscount = (item.discounts || []).some(function (d) {
            return d.title && d.title.indexOf('Birthday Sale') !== -1;
          });
          if (!hasBsDiscount) return;
          var mapEntry = BS_PRODUCT_MAP && BS_PRODUCT_MAP[item.variant_id];
          var mrpUnit = mapEntry && mapEntry.compareAtPrice > item.price ? mapEntry.compareAtPrice : item.price;
          var mrpLine = mrpUnit * item.quantity;
          var paidLine = typeof item.final_line_price === 'number' ? item.final_line_price : item.line_price;
          if (mrpLine > paidLine) savings += (mrpLine - paidLine);
        });

        BS_STATE.cartItems = bsItems;
        BS_STATE.normalCount = normal;
        BS_STATE.premiumCount = premium;
        BS_STATE.totalCount = normal + premium;
        BS_STATE.validCount = normal + Math.min(premium, BS_CONFIG.premiumCap.b4);
        BS_STATE.bsSavings = savings;

        updateUI();
        // ── FUTURE: trigger bundle price UI update here ──

        return BS_STATE;
      });
  }

  // ─────────────────────────────────────────────────────────────────
  // PROGRESS BAR + PREMIUM CAP — updateUI() is the single entry point
  // Called after every syncState() and restoreSelectorsFromCart()
  // ─────────────────────────────────────────────────────────────────

  // persistent unlock state — survives cart re-render (DOM element state does not)
  var _bsMilestoneState = { m1: false, m2: false };

  var BS_PB_TEXTS = [
    'Birthday Sale Live! Add Products to avail offer', // 0
    'Add 1 more & get both products @ ₹799',           // 1
    'Add 2 more to save even more — 4 @ ₹1399 🎉', // 2
    'Add 1 more for your biggest saving: 4 @ ₹1399',          // 3
    'Max saving unlocked — 4 @ ₹1399 🎉'        // 4+
  ];

  function updateProgressBars(validCount) {
    if (!document.querySelector('.bs-pb')) return;

    // Bar position and milestones both use validCount — consistent, no false progress
    var state = Math.min(validCount, 4);
    var pct   = state * 25;

    // When only premium items in cart (no normal), guide user to add non-premium
    var text = (BS_STATE.normalCount === 0 && BS_STATE.premiumCount > 0)
      ? 'Add 1 Non-Premium Perfume & Unlock ₹799'
      : BS_PB_TEXTS[Math.min(validCount, 4)];

    var m1Unlocked = validCount >= 2 && (BS_STATE.premiumCount === 0 || BS_STATE.normalCount >= BS_CONFIG.premiumCap.b2);
    var m2Unlocked = validCount >= 4 && (BS_STATE.premiumCount === 0 || BS_STATE.normalCount >= BS_CONFIG.premiumCap.b4);

    // Mini-cart fill uses --bs-mini-fill CSS variable set on #mini-cart.
    // #mini-cart element persists across section re-renders (only innerHTML changes),
    // so the variable survives and the fill never flashes back to 0%.
    var miniCartEl = document.getElementById('mini-cart');
    if (miniCartEl) miniCartEl.style.setProperty('--bs-mini-fill', pct + '%');

    document.querySelectorAll('.bs-pb').forEach(function (bar) {
      var textEl = bar.querySelector('.bs-pb__text');

      var m1 = bar.querySelector('.bs-pb__milestone--1');
      var m2 = bar.querySelector('.bs-pb__milestone--2');
      if (textEl) textEl.textContent = text;
      if (m1) m1.classList.toggle('bs-pb__milestone--unlocked', m1Unlocked);
      if (m2) m2.classList.toggle('bs-pb__milestone--unlocked', m2Unlocked);

      // m1: new unlock → confetti + lottie; re-render → lottie only; locked → reset
      if (m1Unlocked && !_bsMilestoneState.m1) {
        _bsMilestoneState.m1 = true;
        if (m1 && window.bsPlayMilestoneAnim) window.bsPlayMilestoneAnim(m1);
      } else if (m1Unlocked && _bsMilestoneState.m1) {
        if (m1 && window.bsInitLottie) window.bsInitLottie(m1);
      }
      if (!m1Unlocked) {
        _bsMilestoneState.m1 = false;
        var m1Lottie = m1 && m1.querySelector('.bs-pb__lottie');
        if (m1Lottie) m1Lottie._bsLottieInited = false;
      }

      // m2: confetti + lottie same as m1
      if (m2Unlocked && !_bsMilestoneState.m2) {
        _bsMilestoneState.m2 = true;
        if (m2 && window.bsPlayMilestoneAnim) window.bsPlayMilestoneAnim(m2);
      } else if (m2Unlocked && _bsMilestoneState.m2) {
        if (m2 && window.bsInitLottie) window.bsInitLottie(m2);
      }
      if (!m2Unlocked) {
        _bsMilestoneState.m2 = false;
        var m2Lottie = m2 && m2.querySelector('.bs-pb__lottie');
        if (m2Lottie) m2Lottie._bsLottieInited = false;
      }

      // All bars: set inline style directly (overrides Liquid default, no flicker)
      var fillEl = bar.querySelector('.bs-pb__fill');
      if (fillEl) fillEl.style.width = pct + '%';
    });
  }

  // ─────────────────────────────────────────────────────────────────
  // PREMIUM LIMIT TOAST
  // ─────────────────────────────────────────────────────────────────
  var _toastTimer = null;

  function showPremiumToast(maxPremium) {
    var toast = document.getElementById('bs-premium-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'bs-premium-toast';
      toast.className = 'bs-premium-toast';
      toast.innerHTML =
        '<p class="bs-premium-toast__text"></p>' +
        '<button class="bs-premium-toast__close" type="button" aria-label="Close">&times;</button>';
      toast.querySelector('.bs-premium-toast__close').addEventListener('click', hidePremiumToast);
      document.body.appendChild(toast);
    }
    toast.querySelector('.bs-premium-toast__text').textContent =
      'Offer valid with ' + maxPremium + ' Premium Perfume only';
    toast.classList.add('bs-premium-toast--visible');
    clearTimeout(_toastTimer);
    _toastTimer = setTimeout(hidePremiumToast, 3000);
  }

  function hidePremiumToast() {
    var toast = document.getElementById('bs-premium-toast');
    if (toast) toast.classList.remove('bs-premium-toast--visible');
  }

  function updatePremiumCap() {
    // One premium slot opens per normal product, capped at b4 (2).
    // +1 allows showing premiums one step ahead so user can build toward next deal tier.
    var maxPremium = Math.min(BS_STATE.normalCount + 1, BS_CONFIG.premiumCap.b4);
    var atCap = BS_STATE.premiumCount > 0 && BS_STATE.premiumCount >= maxPremium;

    document.querySelectorAll('.bs-atc-btn').forEach(function (btn) {
      var card = btn.closest('.card-wrapper') || btn.closest('li');
      if (!card) return;
      var isPremium = !!(card.dataset && card.dataset.bsPremium === 'true');
      if (!isPremium) return;

      // Target the li so the grid reflows (no empty gap left behind)
      var li = card.closest('li') || card;

      var wrapper = btn.closest('.card-information__button');
      var isInCart = wrapper && wrapper.classList.contains('bs-in-cart');
      if (isInCart) {
        li.classList.remove('bs-premium-capped');
        return;
      }

      li.classList.toggle('bs-premium-capped', atCap);
    });
  }

  function updatePremiumUpsellVisibility() {
    // Use BS_STATE if already synced; fallback to #bs-cart-counts for non-collection pages
    // where BS_STATE may not be populated yet (async race on cart open)
    var normalCount = BS_STATE.normalCount;
    var premiumCount = BS_STATE.premiumCount;
    if (normalCount === 0 && premiumCount === 0) {
      var countsEl = document.getElementById('bs-cart-counts');
      if (countsEl) {
        normalCount = parseInt(countsEl.dataset.bsNormal || '0', 10);
        premiumCount = parseInt(countsEl.dataset.bsPremium || '0', 10);
      }
    }
    var maxPremium = Math.min(normalCount + 1, BS_CONFIG.premiumCap.b4);
    var atCap = premiumCount > 0 && premiumCount >= maxPremium;
    document.querySelectorAll('li[data-bs-upsell-item]').forEach(function (el) {
      if (el.dataset.bsUpsellPremium === 'true') {
        el.style.display = atCap ? 'none' : '';
      }
    });
  }

 // ─────────────────────────────────────────────────────────────────
  // MINI-CART TIER BAR VISIBILITY — hide .bs-pb.bs-pb--mini when the cart's
  // only Birthday Sale collection items are these 2 specific variants; show
  // as soon as any OTHER Birthday Sale product is present (overrides all).
  // Self-contained (own /cart.js fetch) — does not touch updateProgressBars.
  // ─────────────────────────────────────────────────────────────────
  var BS_PB_MINI_HIDE_VARIANT_IDS = ['42104992006190', '41548272304174'];

  function updateMiniPbVisibility() {
    var bars = document.querySelectorAll('.bs-pb.bs-pb--mini');
    var buzzWrappers = document.querySelectorAll('.bs-cart-buzz-wrapper');
    if (!bars.length && !buzzWrappers.length) return;
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Hide ONLY when the cart has items and every single one of them is one
        // of the 2 excluded variants — no collection membership check at all.
        // Any other product present (BS or not) means show.
        var hideBar = cart.items.length > 0 && cart.items.every(function (item) {
          return BS_PB_MINI_HIDE_VARIANT_IDS.indexOf(String(item.variant_id)) !== -1;
        });
        bars.forEach(function (bar) {
          bar.style.display = hideBar ? 'none' : '';
        });
        buzzWrappers.forEach(function (wrapper) {
          wrapper.style.display = hideBar ? 'none' : '';
        });
      })
      .catch(function () {});
  }

  function updateUI() {
    updateProgressBars(BS_STATE.validCount);
    updatePremiumCap();
    groupBsCartItems();
    updatePremiumUpsellVisibility();
  }

  var _groupBsTimer = null;
  function groupBsCartItems() {
    clearTimeout(_groupBsTimer);
    _groupBsTimer = setTimeout(_doGroupBsCartItems, 250);
  }
  function _doGroupBsCartItems() {
    var cartList = document.querySelector('.mini-cart__navigation');
    if (!cartList) return;

    // Remove old wrapper — move items back to cartList first
    var oldWrapper = cartList.querySelector('.bs-cart-group-wrapper');
    if (oldWrapper) {
      var prevItems = oldWrapper.querySelectorAll('.bs-cart-group-items > li');
      prevItems.forEach(function (li) { cartList.insertBefore(li, oldWrapper); });
      oldWrapper.remove();
    }

    // Re-evaluate data-bs-item using BS_PRODUCT_MAP (authoritative) instead of
    // Liquid discount detection (unreliable when discount re-distributes on qty change)
    // if (BS_PRODUCT_MAP && Object.keys(BS_PRODUCT_MAP).length > 0) {
    //   cartList.querySelectorAll('li[data-variant-id]').forEach(function (li) {
    //     var variantId = parseInt(li.dataset.variantId);
    //     if (BS_PRODUCT_MAP.hasOwnProperty(variantId)) {
    //       li.dataset.bsItem = 'true';
    //     } else {
    //       delete li.dataset.bsItem;
    //     }
    //   });
    // }

    // data-bs-item is trusted as-is from Liquid (mini-cart-revamp.liquid) — it's set
    // only when the line actually has a "Birthday Sale" discount applied, which is the
    // correct scope for this group (collection membership alone is NOT enough — a BS
    // product sitting in the cart without the deal discount applied should not be
    // grouped in here).

    var bsItems = cartList.querySelectorAll('li[data-bs-item="true"]');
    if (bsItems.length === 0) {
      document.querySelectorAll('.gokwik-checkout button').forEach(function (btn) {
        btn.disabled = false;
        btn.classList.remove('disabled');
      });
      return;
    }

    // Wrapper li — acts as the box
    var wrapper = document.createElement('li');
    wrapper.className = 'bs-cart-group-wrapper';

    // Header inside the box
    var headerDiv = document.createElement('div');
    headerDiv.className = 'bs-cart-group-header';
    var savingsRs = BS_STATE.bsSavings > 0 ? ' ₹' + Math.round(BS_STATE.bsSavings / 100) : '';
    headerDiv.innerHTML =
          '<img src="https://cdn.shopify.com/s/files/1/0054/6665/2718/files/Vector.svg?v=1782394202" width="11" height="11" alt="icon" />' +
          '<span>You are saving' + savingsRs + '</span>';

    // Inner list to hold BS items — use fragment so all moves = 1 DOM mutation
    var innerList = document.createElement('ul');
    innerList.className = 'bs-cart-group-items';
    var frag = document.createDocumentFragment();
    bsItems.forEach(function (li) { frag.appendChild(li); });
    innerList.appendChild(frag);

    wrapper.appendChild(headerDiv);
    wrapper.appendChild(innerList);

    // Prepend the whole box to cart — single mutation
    cartList.insertBefore(wrapper, cartList.firstChild);

    // Force-enable checkout button immediately after our DOM mutations —
    // GokWik ka 700ms debounce wait avoid karne ke liye
    document.querySelectorAll('.gokwik-checkout button').forEach(function (btn) {
      btn.disabled = false;
      btn.classList.remove('disabled');
    });
  }

  // ─────────────────────────────────────────────────────────────────
  // UI HELPERS
  // ─────────────────────────────────────────────────────────────────

  // Find card wrapper containing the ATC/qty for a variantId
  function findCardWrapper(variantId) {
    var atcBtn = document.querySelector('.bs-atc-btn[data-variant-id="' + variantId + '"]');
    return atcBtn ? atcBtn.closest('.card-information__button') : null;
  }

  function showQtySelector(wrapper, qty) {
    var qtySelector = wrapper.querySelector('.bs-qty-selector');
    if (!qtySelector) return;
    qtySelector.querySelector('.bs-qty-count').textContent = qty;
    qtySelector.classList.add('bs-active');
    wrapper.classList.add('bs-in-cart');
  }

  function showAtcBtn(wrapper) {
    var qtySelector = wrapper.querySelector('.bs-qty-selector');
    if (!qtySelector) return;
    qtySelector.classList.remove('bs-active');
    wrapper.classList.remove('bs-in-cart');
  }

  function updateQtyDisplay(wrapper, qty) {
    var countEl = wrapper.querySelector('.bs-qty-count');
    if (countEl) countEl.textContent = qty;
  }

  // ─────────────────────────────────────────────────────────────────
  // EVENT: ATC success → show qty selector + bind buttons
  // Existing <add-to-cart> fires ajaxProduct:added after successful add
  // ─────────────────────────────────────────────────────────────────
  document.addEventListener('ajaxProduct:added', function (e) {
    var product = e.detail && e.detail.product;
    if (!product) return;

    var variantId = product.variant_id || (product.variants && product.variants[0] && product.variants[0].id);
    if (!variantId) return;

    // Update ALL wrappers for this variant (card on collection page + active QV sheet)
    var isBsProduct = false;
    document.querySelectorAll('.bs-atc-btn[data-variant-id="' + variantId + '"]').forEach(function (btn) {
      isBsProduct = true;
      var wrapper = btn.closest('.card-information__button');
      if (!wrapper) return;
      var qtySelector = wrapper.querySelector('.bs-qty-selector');
      if (qtySelector) bindQtySelector(qtySelector);
      showQtySelector(wrapper, 1);
    });

    syncState();
  });

  // ─────────────────────────────────────────────────────────────────
  // BIND QTY BUTTONS — direct listeners on each button
  // Direct binding beats document delegation when ancestors stopPropagation
  // ─────────────────────────────────────────────────────────────────
  function bindQtySelector(qtySelector) {
    if (qtySelector.dataset.bsInit === '1') return;
    qtySelector.dataset.bsInit = '1';

    var wrapper = qtySelector.closest('.card-information__button');
    var variantId = qtySelector.dataset.variantId;
    if (!wrapper || !variantId) return;

    var plusBtn = qtySelector.querySelector('.bs-qty-plus');
    var minusBtn = qtySelector.querySelector('.bs-qty-minus');

    // Disable both buttons + show spinner on selector (prevents race conditions)
    function setLoading(on) {
      qtySelector.classList.toggle('bs-loading', on);
      if (plusBtn) plusBtn.disabled = on;
      if (minusBtn) minusBtn.disabled = on;
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        // Block if premium cap reached for this product
        var card = qtySelector.closest('.card-wrapper') || qtySelector.closest('li');
        var isPremium = card
          ? !!card.querySelector('.plp-badge-premium')
          : (qtySelector.closest('.bs-qv-sheet') || {}).dataset && qtySelector.closest('.bs-qv-sheet').dataset.premium === 'true';
        if (isPremium) {
          var maxPremium = BS_STATE.totalCount <= 2
            ? BS_CONFIG.premiumCap.b2
            : BS_CONFIG.premiumCap.b4;
            //console.log("maxPremium:", BS_STATE.premiumCount, maxPremium);
          if ((BS_STATE.premiumCount >= maxPremium && $("span.cart-count-bubble span[aria-hidden]")[0].innerText == "1" ) || BS_STATE.premiumCount == 2) {
            showPremiumToast(maxPremium);
            return; 
          } 
        }

        var currentQty = parseInt(wrapper.querySelector('.bs-qty-count').textContent) || 0;
        var newQty = currentQty + 1;
        setLoading(true);

        var isInQv = !!qtySelector.closest('.bs-qv-sheet');
        cartAdd(variantId)
          .then(function (parsedState) {
            updateQtyDisplay(wrapper, newQty);
            if (isInQv && window.closeQuickView) {
              setTimeout(window.closeQuickView, 300);
            }
            openDrawer(parsedState);
            syncState();
          })
          .finally(function () { setLoading(false); });
      });
    }

    if (minusBtn) {
      minusBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var currentQty = parseInt(wrapper.querySelector('.bs-qty-count').textContent) || 0;
        var newQty = currentQty - 1;
        setLoading(true);

        cartChange(variantId, newQty)
          .then(function (parsedState) {
            if (newQty <= 0) {
              showAtcBtn(wrapper);
            } else {
              updateQtyDisplay(wrapper, newQty);
            }
            // Update all cart sections (drawer content + bubble) without opening drawer
            if (miniCart && parsedState && parsedState.sections) {
              miniCart.getSectionsToRender().forEach(function (section) {
                var elementId = section.elementId || section.id;
                var el = document.getElementById(elementId);
                if (el && parsedState.sections[section.id]) {
                  el.innerHTML = miniCart.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
                }
              });
            }
            syncState();
          })
          .finally(function () { setLoading(false); });
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // RESTORE SELECTORS — update all qty selectors + BS_STATE + UI
  // Accepts a Shopify cart object (from /cart.js or cart_updated event)
  // Avoids an extra /cart.js fetch by deriving state from the passed cart
  // ─────────────────────────────────────────────────────────────────
  function restoreSelectorsFromCart(cart) {
    // If cart data not provided in event detail, fall back to a fresh /cart.js fetch
    if (!cart || !cart.items) { syncState(); return; }

    // Sum (not overwrite) — Shopify can keep the same variant split across
    // multiple lines when line item properties differ between add.js calls.
    var cartQtyMap = {};
    cart.items.forEach(function (item) {
      cartQtyMap[item.variant_id] = (cartQtyMap[item.variant_id] || 0) + item.quantity;
    });

    var bsItems = [], normal = 0, premium = 0;
    var _restoreSeen = {};
    document.querySelectorAll('.bs-qty-selector').forEach(function (qtySelector) {
      var variantId = parseInt(qtySelector.dataset.variantId);
      if (!variantId || _restoreSeen[variantId]) return;
      var qty = cartQtyMap[variantId] || 0;
      if (qty === 0) return;
      _restoreSeen[variantId] = true;
      var card = qtySelector.closest('.card-wrapper') || qtySelector.closest('li');
      var isPremium = !!(card && (
        card.dataset.bsPremium === 'true' || card.querySelector('.plp-badge-premium')
      ));
      bsItems.push({ variantId: variantId, qty: qty, isPremium: isPremium });
      if (isPremium) { premium += qty; } else { normal += qty; }
    });

    // Fallback for non-collection pages: read Liquid-rendered counts from mini-cart
    if (normal === 0 && premium === 0) {
      var restoreCountsEl = document.getElementById('bs-cart-counts');
      if (restoreCountsEl) {
        normal = parseInt(restoreCountsEl.dataset.bsNormal || '0', 10);
        premium = parseInt(restoreCountsEl.dataset.bsPremium || '0', 10);
      }
    }

    // if (BS_PRODUCT_MAP && Object.keys(BS_PRODUCT_MAP).length > 0) {
    //   normal = 0; premium = 0;
    //   cart.items.forEach(function (item) {
    //     if (BS_PRODUCT_MAP.hasOwnProperty(item.variant_id)) {
    //       if (BS_PRODUCT_MAP[item.variant_id]) premium += item.quantity;
    //       else normal += item.quantity;
    //     }
    //   });
    // }

    // var savings = 0;
    // cart.items.forEach(function (item) {
    //   if (!item.discounts) return;
    //   item.discounts.forEach(function (d) {
    //     if (d.title && d.title.indexOf('Birthday Sale') !== -1) savings += d.amount;
    //   });
    // });
    // Savings = MRP (compare_at_price) minus what's actually paid — see syncState() for
    // the full explanation (same discount-applied scope + final_line_price usage).
    var savings = 0;
    cart.items.forEach(function (item) {
      var hasBsDiscount = (item.discounts || []).some(function (d) {
        return d.title && d.title.indexOf('Birthday Sale') !== -1;
      });
      if (!hasBsDiscount) return;
      var mapEntry = BS_PRODUCT_MAP && BS_PRODUCT_MAP[item.variant_id];
      var mrpUnit = mapEntry && mapEntry.compareAtPrice > item.price ? mapEntry.compareAtPrice : item.price;
      var mrpLine = mrpUnit * item.quantity;
      var paidLine = typeof item.final_line_price === 'number' ? item.final_line_price : item.line_price;
      if (mrpLine > paidLine) savings += (mrpLine - paidLine);
    });

    BS_STATE.cartItems = bsItems;
    BS_STATE.normalCount = normal;
    BS_STATE.premiumCount = premium;
    BS_STATE.totalCount = normal + premium;
    BS_STATE.validCount = normal + Math.min(premium, BS_CONFIG.premiumCap.b4);
    BS_STATE.bsSavings = savings;

    // Restore qty selector UI
    document.querySelectorAll('.bs-qty-selector').forEach(function (qtySelector) {
      var variantId = parseInt(qtySelector.dataset.variantId);
      var wrapper = qtySelector.closest('.card-information__button');
      if (!wrapper) return;
      var qty = cartQtyMap[variantId] || 0;
      if (qty > 0) { showQtySelector(wrapper, qty); }
      else { showAtcBtn(wrapper); }
    });

    updateUI();
  }

  // Fetch cart then restore (used on page load)
  function syncAndRestoreSelectors() {
    syncState().then(function () {
      document.querySelectorAll('.bs-qty-selector').forEach(function (qtySelector) {
        var variantId = parseInt(qtySelector.dataset.variantId);
        var wrapper = qtySelector.closest('.card-information__button');
        if (!wrapper) return;

        var found = null;
        BS_STATE.cartItems.forEach(function (item) {
          if (item.variantId === variantId) found = item;
        });

        if (found) {
          showQtySelector(wrapper, found.qty);
        } else {
          showAtcBtn(wrapper);
        }
      });
    });
  }

  // ─────────────────────────────────────────────────────────────────
  // INIT — bind buttons + sync on page load
  // ─────────────────────────────────────────────────────────────────
  function init() {
    // Fetches BS_PRODUCT_MAP (variant → isPremium/price/compareAtPrice). Cheap on
    // repeat init() calls — cached in sessionStorage, only fetches once per session.
    initProductMap();
    document.querySelectorAll('.bs-qty-selector').forEach(function (qtySelector) {
      bindQtySelector(qtySelector);
    });
    syncAndRestoreSelectors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after infinite scroll / filter
  document.addEventListener('theme:infinite-scroll:loaded', init);
  document.addEventListener('collection:updated', init);

  // ─────────────────────────────────────────────────────────────────
  // EXTERNAL CART CHANGES
  // custom-event-cart-update.js fires window 'cart_updated' after
  // every /cart/* request — detail already has fresh cart data,
  // use it directly to avoid a redundant /cart.js fetch
  // ─────────────────────────────────────────────────────────────────
  // cart_updated fires after any /cart/ API call (CartWatcher).
  // restoreSelectorsFromCart handles both state sync + selector UI using event cart data.
  window.addEventListener('cart_updated', function (e) {
    restoreSelectorsFromCart(e.detail);
  });

  document.addEventListener('cartdrawer:opened', function () {
    syncState().then(function () {
      groupBsCartItems();
      updatePremiumUpsellVisibility();
    });
  });

  document.addEventListener('cart-recommendations:updated', function () {
    updatePremiumUpsellVisibility();
  });

  // Fix CartItems.enableLoading — uses DOM order which breaks after groupBsCartItems() reorders li elements.
  // Patch: find loading overlay via data-index of quantity input instead of DOM position.
  customElements.whenDefined('cart-items').then(function () {
    var Proto = customElements.get('cart-items').prototype;
    Proto.enableLoading = function (line) {
      var cartItemsEl = document.getElementById('main-cart-items');
      if (cartItemsEl) cartItemsEl.classList.add('cart__items--disabled');
      var input = this.querySelector('[name="updates[]"][data-index="' + line + '"]');
      if (input) {
        var li = input.closest('li');
        if (li) {
          var overlay = li.querySelector('.loading-overlay');
          if (overlay) overlay.classList.remove('hidden');
        }
      }
      document.activeElement.blur();
      if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
    };
  });

  // ─────────────────────────────────────────────────────────────────
  // QUICK VIEW — bottom sheet open/close + carousel + ATC handler
  // ─────────────────────────────────────────────────────────────────
  var _qvPanel   = document.getElementById('bs-qv-panel');
  var _qvOverlay = document.getElementById('bs-qv-overlay');
  var _qvClose   = document.getElementById('bs-qv-close');
  var _qvActiveHandle = null;

  function openQuickView(handle) {
    if (!_qvPanel || !_qvOverlay) return;

    // Hide previously active sheet
    if (_qvActiveHandle) {
      var prev = document.getElementById('bs-qv-' + _qvActiveHandle);
      if (prev) prev.classList.remove('bs-qv-active');
    }

    var sheet = document.getElementById('bs-qv-' + handle);
    if (!sheet) return;

    sheet.classList.add('bs-qv-active');
    sheet.removeAttribute('aria-hidden');
    _qvActiveHandle = handle;

    sheet.scrollTop = 0;

    _qvOverlay.classList.add('bs-qv-open');
    _qvOverlay.removeAttribute('aria-hidden');
    _qvPanel.classList.add('bs-qv-open');
    _qvPanel.removeAttribute('aria-hidden');

    document.body.style.overflow = 'hidden';

    // Re-init fragrance tab indicator (was hidden, offsetWidth was 0 on load)
    requestAnimationFrame(function () {
      window.dispatchEvent(new Event('resize'));
    });

    _initQvCarousel(sheet);
  }

  function closeQuickView() {
    if (!_qvPanel || !_qvOverlay) return;
    _qvOverlay.classList.remove('bs-qv-open');
    _qvOverlay.setAttribute('aria-hidden', 'true');
    _qvPanel.classList.remove('bs-qv-open');
    _qvPanel.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    if (_qvActiveHandle) {
      var sheet = document.getElementById('bs-qv-' + _qvActiveHandle);
      if (sheet) {
        sheet.classList.remove('bs-qv-active');
        sheet.setAttribute('aria-hidden', 'true');
      }
      _qvActiveHandle = null;
    }
  }
  window.closeQuickView = closeQuickView;

  // Initialise carousel scroll + dot sync for a given sheet
  function _initQvCarousel(sheet) {
    var imagesEl = sheet.querySelector('.bs-qv-images');
    var dotsEl   = sheet.querySelector('.bs-qv-dots');
    if (!imagesEl) return;

    // Reset scroll to first image
    imagesEl.scrollLeft = 0;

    if (!dotsEl) return;
    var dots = dotsEl.querySelectorAll('.bs-qv-dot');
    if (!dots.length) return;

    // Prevent binding twice
    if (imagesEl.dataset.qvInit === '1') {
      dots.forEach(function (d) { d.classList.toggle('bs-qv-dot--active', d.dataset.index === '0'); });
      return;
    }
    imagesEl.dataset.qvInit = '1';

    function setActiveDot(idx) {
      dots.forEach(function (d) {
        d.classList.toggle('bs-qv-dot--active', parseInt(d.dataset.index) === idx);
      });
    }

    // Scroll sync → dots (offsetLeft-based for peek carousel)
    imagesEl.addEventListener('scroll', function () {
      var slides = imagesEl.children;
      var step = slides.length > 1 ? slides[1].offsetLeft : (slides[0] ? slides[0].offsetWidth : imagesEl.offsetWidth);
      var idx = Math.round(imagesEl.scrollLeft / step);
      setActiveDot(idx);
    }, { passive: true });

    // Dot click → scroll using slide's offsetLeft so peek gap is respected
    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var idx = parseInt(dot.dataset.index);
        var slide = imagesEl.children[idx];
        imagesEl.scrollTo({ left: slide ? slide.offsetLeft : idx * imagesEl.offsetWidth, behavior: 'smooth' });
        setActiveDot(idx);
      });
    });
  }

  // Card click → open QV (skip ATC / qty selector area)
  document.addEventListener('click', function (e) {
    if (e.target.closest('.card-information__button')) return;
    var trigger = e.target.closest('[data-bs-qv]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    openQuickView(trigger.dataset.bsQv);
  });

  // Close on overlay click or close button
  if (_qvOverlay) _qvOverlay.addEventListener('click', closeQuickView);
  if (_qvClose)   _qvClose.addEventListener('click', closeQuickView);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && _qvActiveHandle) closeQuickView();
  });

  // Close quick view shortly after ATC/qty+ click — before cart drawer opens
  if (_qvPanel) {
    _qvPanel.addEventListener('click', function (e) {
      if (e.target.closest('.bs-atc-btn') || e.target.closest('.bs-qty-plus')) {
        setTimeout(closeQuickView, 1000);
      }
    });
  }

})();

// ── Sale Countdown Timer ─────────────────────────────────────────────────────
// Supports multiple simultaneous .bs-timer instances (page-level from
// bs-sale-timer-section.liquid + the mini-cart drawer's copy rendered with
// id_suffix: 'cart' in mini-cart-revamp.liquid) — each gets its own
// independent tick loop with scoped lookups, so duplicate/suffixed IDs never
// clash. The drawer's HTML is fetched + injected into the DOM well after this
// script runs (Shopify Section Rendering API), so a one-time querySelectorAll
// at load wouldn't find it — a MutationObserver picks up any .bs-timer added
// later, anywhere on the page.
(function () {
  function initTimer(el) {
    if (el._bsTimerInited) return;
    el._bsTimerInited = true;

    // Read config from data attributes (set in Liquid snippet)
    var saleEnd   = new Date(el.dataset.saleEnd);
    var buzzFrom  = el.dataset.buzzFrom;   // 'YYYY-MM-DD'
    var buzzTo    = el.dataset.buzzTo;     // 'YYYY-MM-DD'
    var buzzSh    = parseInt(el.dataset.buzzSh, 10);  // start hour (20)
    var buzzSm    = parseInt(el.dataset.buzzSm, 10);  // start min  (0)
    var buzzEh    = parseInt(el.dataset.buzzEh, 10);  // end hour   (21)
    var buzzEm    = parseInt(el.dataset.buzzEm, 10);  // end min    (20)

    var labelEl = el.querySelector('.bs-timer__label');
    var dotEl   = el.querySelector('.bs-timer__dot');
    var subEl   = el.querySelector('.bs-timer__sub');
    var daysEl  = el.querySelector('[id^="bs-timer-days"]');
    var hrsEl   = el.querySelector('[id^="bs-timer-hrs"]');
    var minEl   = el.querySelector('[id^="bs-timer-min"]');
    var secEl   = el.querySelector('[id^="bs-timer-sec"]');

    function pad(n) { return n < 10 ? '0' + n : '' + n; }

    // Get IST date string 'YYYY-MM-DD' for a given JS Date
    function istDateStr(date) {
      var ist = new Date(date.getTime() + (5.5 * 3600000));
      return ist.toISOString().slice(0, 10);
    }

    // Check if now is inside the buzz window for today
    function getBuzzTarget() {
      var now     = new Date();
      var today   = istDateStr(now);

      if (today < buzzFrom || today > buzzTo) return null;

      // Build today's buzz start/end in IST
      var startStr = today + 'T' + pad(buzzSh) + ':' + pad(buzzSm) + ':00+05:30';
      var endStr   = today + 'T' + pad(buzzEh) + ':' + pad(buzzEm) + ':00+05:30';
      var start    = new Date(startStr);
      var end      = new Date(endStr);

      if (now >= start && now < end) return end;
      return null;
    }

    var _lastMode = null;

    function tick() {
      var now        = new Date();
      var buzzTarget = getBuzzTarget();
      var target, mode;

      if (buzzTarget) {
        target = buzzTarget;
        mode   = 'buzz';
      } else {
        target = saleEnd;
        mode   = 'sale';
      }

      // Update label only on mode switch (avoid DOM thrash every second)
      if (mode !== _lastMode) {
        if (mode === 'buzz') {
          labelEl.textContent      = 'Buzz Offer Ends';
          labelEl.style.color      = '#ff9c1c';
          dotEl.style.background   = '#ff9c1c';
          subEl.textContent        = 'Extra ₹80 off — Hurry!';
        } else {
          labelEl.textContent      = 'Sale Ends In';
          labelEl.style.color      = '#de1612';
          dotEl.style.background   = '#de1612';
          subEl.textContent        = "Don't miss out";
        }
        _lastMode = mode;
      }

      var diff = Math.max(0, target - now);
      var days = Math.floor(diff / 86400000);
      var hrs  = Math.floor((diff % 86400000) / 3600000);
      var mins = Math.floor((diff % 3600000)  / 60000);
      var secs = Math.floor((diff % 60000)    / 1000);

      daysEl.textContent = pad(days);
      hrsEl.textContent  = pad(hrs);
      minEl.textContent  = pad(mins);
      secEl.textContent  = pad(secs);

      if (diff > 0) setTimeout(tick, 1000);
    }

    tick();
  }

  function scanForTimers() {
    document.querySelectorAll('.bs-timer').forEach(initTimer);
  }

  scanForTimers();
  new MutationObserver(scanForTimers).observe(document.body, { childList: true, subtree: true });
})();

// ── Cart Buzz Timer ──────────────────────────────────────────────────────────
(function () {
  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function istDateStr(date) {
    var ist = new Date(date.getTime() + (5.5 * 3600000));
    return ist.toISOString().slice(0, 10);
  }

  function tick() {
    var el = document.getElementById('bs-cart-buzz');
    if (!el) { setTimeout(tick, 1000); return; }

    var buzzFrom = el.dataset.buzzFrom;
    var buzzTo   = el.dataset.buzzTo;
    var buzzSh   = parseInt(el.dataset.buzzSh, 10);
    var buzzSm   = parseInt(el.dataset.buzzSm, 10);
    var buzzEh   = parseInt(el.dataset.buzzEh, 10);
    var buzzEm   = parseInt(el.dataset.buzzEm, 10);

    var now   = new Date();
    var today = istDateStr(now);

    if (today < buzzFrom || today > buzzTo) {
      el.style.display = 'none';
      setTimeout(tick, 5000);
      return;
    }

    var start = new Date(today + 'T' + pad(buzzSh) + ':' + pad(buzzSm) + ':00+05:30');
    var end   = new Date(today + 'T' + pad(buzzEh) + ':' + pad(buzzEm) + ':00+05:30');

    if (now < start || now >= end) {
      el.style.display = 'none';
      setTimeout(tick, 5000);
      return;
    }

    el.style.display = 'flex';

    var minEl = document.getElementById('bs-cart-buzz-min');
    var secEl = document.getElementById('bs-cart-buzz-sec');
    var diff  = Math.max(0, end - now);
    if (minEl) minEl.textContent = pad(Math.floor(diff / 60000));
    if (secEl) secEl.textContent = pad(Math.floor((diff % 60000) / 1000));

    if (diff > 0) {
      setTimeout(tick, 1000);
    } else {
      el.style.display = 'none';
    }
  }

  tick(); // deferred script hai — DOM already ready hai, direct call safe hai
})();

// ── Lottie init (idempotent — safe to call on every cart update) ──
window.bsInitLottie = function (milestoneEl) {
  var lottieEl     = milestoneEl.querySelector('.bs-pb__lottie');
  var lottieTickEl = milestoneEl.querySelector('.bs-pb__lottie--tick');
  if (!lottieEl || lottieEl._bsLottieInited) return;
  var src     = lottieEl.dataset.lottieSrc;
  var tickSrc = lottieTickEl ? lottieTickEl.dataset.lottieSrc : null;
  if (!src || src === 'LOTTIE_JSON_CDN_URL') return;
  lottieEl._bsLottieInited = true;

  function loadTickAnim() {
    if (!lottieTickEl || !tickSrc) return;
    lottieTickEl.style.display = 'block';
    var circleEl = lottieTickEl.closest('.bs-pb__milestone-circle');
    if (circleEl) circleEl.classList.add('bs-tick-active');
    fetch(tickSrc)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        function startTick() {
          var tickAnim = window.lottie.loadAnimation({
            container: lottieTickEl,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData: data
          });
          setTimeout(function () {
            tickAnim.pause(); // freeze on last frame
          }, 1500);
        }
        if (window.lottie) { startTick(); }
        else {
          var t = setInterval(function () {
            if (window.lottie) { clearInterval(t); startTick(); }
          }, 100);
        }
      })
      .catch(function () {});
  }

  function loadAnim(data) {
    var anim = window.lottie.loadAnimation({
      container: lottieEl,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: data
    });
    setTimeout(function () {
      anim.pause();      // pehla pause on last frame
      loadTickAnim();    // phir doosra start
    }, 1500);
  }

  fetch(src)
    .then(function (r) { return r.json(); })
    .then(function (data) {
      if (window.lottie) {
        loadAnim(data);
      } else {
        var t = setInterval(function () {
          if (window.lottie) { clearInterval(t); loadAnim(data); }
        }, 100);
      }
    })
    .catch(function () {});
};

// ── Milestone confetti → Lottie sequence ──
var _bsConfettiLastFired = 0;
window.bsPlayMilestoneAnim = function (milestoneEl) {
  var confettiEl = milestoneEl.querySelector('.bs-pb__icon--confetti');
  var lottieEl   = milestoneEl.querySelector('.bs-pb__lottie');

  // Step 1: confetti centered inside cart drawer — 3s cooldown prevents double-fire
  var now = Date.now();
  if (confettiEl && confettiEl.src && confettiEl.src.indexOf('CONFETTI_GIF_CDN_URL') === -1 && (now - _bsConfettiLastFired > 3000)) {
    _bsConfettiLastFired = now;
    var drawerEl = document.getElementById('mini-cart') || document.querySelector('.cart-drawer');
    var target = drawerEl || document.body;
    var gif = document.createElement('img');
    gif.src = confettiEl.src;
    gif.style.cssText = 'position:absolute;z-index:99999;pointer-events:none;' +
      'left:50%;top:20%;transform:translate(-50%,-50%);width:auto;height:auto;';
    target.appendChild(gif);
    setTimeout(function () { gif.parentNode && gif.parentNode.removeChild(gif); }, 1500);
  }

  // Step 2: init lottie after confetti (2.5s delay, then bsInitLottie handles it)
  setTimeout(function () {
    if (window.bsInitLottie) window.bsInitLottie(milestoneEl);
  }, 1500);
};
